import UtilityService from './utility-service';

const API_URL =
  process.env.NODE_ENV === 'development'
    ? process.env.NEXT_PUBLIC_API_URL_DEVELOPMENT
    : UtilityService.getApiUrl();

const AURORA_API_URL =
  process.env.NODE_ENV === 'development'
    ? process.env.NEXT_PUBLIC_AURORA_API_URL_DEVELOPMENT
    : UtilityService.getAuroraApiUrl();

export class RequestJsonError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'RequestJsonError';
  }
}

const requestJson = async <T>(
  endpoint: string,
  method: 'GET' | 'POST',
  body?: unknown,
  aurora: boolean = false,
  signal?: AbortSignal,
): Promise<T> => {
  const response = await fetch(`${!aurora ? API_URL : AURORA_API_URL}${endpoint}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    signal,
    ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
  });

  let payload: any;
  try {
    payload = await response.json();
  } catch {
    throw new RequestJsonError(
      response.ok
        ? 'The server returned an invalid response.'
        : `Request failed with status ${response.status}.`,
      response.status,
    );
  }

  if (!response.ok || payload?.error) {
    throw new RequestJsonError(
      typeof payload?.error === 'string'
        ? payload.error
        : `Request failed with status ${response.status}.`,
      response.status,
      payload,
    );
  }

  return payload as T;
};

const Request = {
  buildUrl(endpoint: string, aurora: boolean = false): string {
    return `${!aurora ? API_URL : AURORA_API_URL}${endpoint}`;
  },

  async post(endpoint: string, body: any, aurora: boolean = false): Promise<Response | undefined> {
    try {
      let config: RequestInit;

      if (body instanceof FormData) {
        config = {
          method: 'POST',
          credentials: 'include',
          body,
        };
      } else {
        config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          } as HeadersInit,
          credentials: 'include',
          body: JSON.stringify(body),
        };
      }

      const response = await fetch(Request.buildUrl(endpoint, aurora), config);

      if (!response.ok) {
        // Allow callers to inspect Aurora limit and upload-style error responses.
        if (response.status === 429 || body instanceof FormData) {
          return response;
        }
        throw new Error(`Error on POST request. Status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },

  async get(endpoint: string, aurora: boolean = false): Promise<Response | undefined> {
    try {
      const config: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        } as HeadersInit,
        credentials: 'include',
      };

      const response = await fetch(Request.buildUrl(endpoint, aurora), config);

      if (!response.ok) {
        // Allow callers to handle 429 (limit reached) for Aurora endpoints
        if (aurora && response.status === 429) {
          return response;
        }
        throw new Error(`Error on GET request. Status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },

  async getJsonOrThrow<T>(
    endpoint: string,
    aurora: boolean = false,
    signal?: AbortSignal,
  ): Promise<T> {
    return requestJson<T>(endpoint, 'GET', undefined, aurora, signal);
  },

  async postJsonOrThrow<T>(
    endpoint: string,
    body: unknown,
    aurora: boolean = false,
    signal?: AbortSignal,
  ): Promise<T> {
    return requestJson<T>(endpoint, 'POST', body, aurora, signal);
  },

  async put(endpoint: string, body: any, aurora: boolean = false): Promise<Response | undefined> {
    try {
      let config: RequestInit;

      if (body instanceof FormData) {
        config = {
          method: 'PUT',
          credentials: 'include',
          body,
        };
      } else {
        config = {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          } as HeadersInit,
          credentials: 'include',
          body: JSON.stringify(body),
        };
      }

      const response = await fetch(Request.buildUrl(endpoint, aurora), config);

      if (!response.ok) {
        throw new Error(`Error on PUT request. Status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },

  async patch(endpoint: string, body: any, aurora: boolean = false): Promise<Response | undefined> {
    try {
      let config: RequestInit;

      if (body instanceof FormData) {
        config = {
          method: 'PATCH',
          credentials: 'include',
          body,
        };
      } else {
        config = {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          } as HeadersInit,
          credentials: 'include',
          body: JSON.stringify(body),
        };
      }

      const response = await fetch(Request.buildUrl(endpoint, aurora), config);

      if (!response.ok) {
        throw new Error(`Error on PATCH request. Status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },

  async delete(
    endpoint: string,
    body: any,
    aurora: boolean = false,
  ): Promise<Response | undefined> {
    try {
      const config: RequestInit = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        } as HeadersInit,
        credentials: 'include',
        body: JSON.stringify(body),
      };

      const response = await fetch(Request.buildUrl(endpoint, aurora), config);

      if (!response.ok) {
        throw new Error(`Error on DELETE request. Status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },
};

export default Request;
